/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { Avatar, Grid } from '@mui/material';
import { DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { AccessRoleDTO, AccessRoleFilterDTO, CompanyDTO, CompanyFilterDTO, DepartmentFilterDTO, PeopleDTO, UserDTO, UserFilterDTO, ValueListDTO, ValueListFilterDTO } from '../../../shared/interfaces';
import { formatDateTime } from '../../../shared/utils';
import { useTranslation } from 'react-i18next';

interface IPeopleMaintenanceProps {
    data: any;
    parentId?: string;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const PeopleMaintenance: React.FC<IPeopleMaintenanceProps> = ({ data, parentId, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {


    const [typeList, setTypeList] = useState<ValueListDTO[]>([]);
    const [statusList, setStatusList] = useState<ValueListDTO[]>([]);
    const [departmentList, setDepartmentList] = useState<ValueListDTO[]>([]);
    const [jobTitleList, setJobTitleList] = useState<ValueListDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);
    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [peopleDTO, setPeopleDTO] = useState<PeopleDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const { t } = useTranslation();

    useEffect(() => {
        setPeopleDTO({
            ...peopleDTO,
            id: '',
            name: '',
            fullName: '',
            description: '',
            typeId: '',
            statusId: '',
            email: '',
            phone: '',
            mobile: '',
            departmentId:'',
            jobTitleId: '',
            avatar: '',
            companyId: parentId ?? ''
        } as PeopleDTO);
    }, [data, isOpenMaintenance]);

    useEffect(() => {
        if (isOpenMaintenance) {
            handleListGetAll(ListId.corpsphere.people.typeId, setTypeList);
            handleListGetAll(ListId.corpsphere.people.statusId, setStatusList);
            handleListGetAll(ListId.corpsphere.people.jobTitle, setJobTitleList);
            handleCompanyGetAll();
            handleDepartmentGetAll();
            handlerUserGetAllAsync();
            handleGetPeopleByIdAsync();

            handleGetPermission(ListId.corpsphere.pleopleId, setAccessRolePermissionDTO);
        }
    }, [data, isOpenMaintenance]);

    const handleAvatarEditClick = () => {
        const fileInput = document.getElementById('avatar-input');
        fileInput?.click(); // Programmatically click the hidden file input
    };

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync', accessRoleFilterDTO, setActionExecuted, setLoading, undefined, setList,);
    }, []);

    const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Reduzir a imagem antes de atualizar o estado
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (e) => {
                const img = document.createElement('img');
                img.src = e.target?.result as string;

                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    // Ajuste a largura e altura desejadas
                    canvas.width = 100;
                    canvas.height = 100;

                    ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

                    const newDataUrl = canvas.toDataURL('image/jpeg', 0.75);
                    setPeopleDTO({
                        ...peopleDTO,
                        avatar: newDataUrl // atualiza o estado com a imagem reduzida
                    } as PeopleDTO);
                };
            };
        }
    };

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO: ValueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setList, showMessage);
    }, []);

    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);

    const handleDepartmentGetAll = useCallback(async () => {
        const departmentFilterDTO: DepartmentFilterDTO = {
            name: '',
            responsibleId: '',
            companyId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.department', departmentFilterDTO, setLoading, setDepartmentList, showMessage);
    }, []);

    const handlerUserGetAllAsync = useCallback(async () => {
        const userFilterDTO: UserFilterDTO = {
            companyId: '',
            peopleId: '',
            userName: '',
            isServiceAccount: false
        };
        GenericService.GetAllViewAsync1('authsphere.user', userFilterDTO, setActionExecuted, setLoading, setUserList, showMessage, false);

    }, []);

    const handleGetPeopleByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('corpsphere.people', data.id, setLoading, setPeopleDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {
        const dto = peopleDTO as PeopleDTO;

        if (peopleDTO?.id === '' || peopleDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('corpsphere.people', dto, setPeopleDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            if (!accessRolePermissionDTO?.allowUpdate) {
                showMessage(t('message.allowUpdate'), 'info');
                return;
            }
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('corpsphere.people', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [peopleDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);

        if (type != 'success')
            setIsOpenMaintenance(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: t('people.maintenanceButton.save'), icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick },
        { id: 'close', text: t('people.maintenanceButton.close'), icon: 'close_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title={t('people.detailsName')}
                icon='people_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3} lg={2}>
                            <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="avatar-input"
                                type="file"
                                onChange={handleAvatarChange}
                            />
                            <Avatar
                                style={{ width: '150px', height: '150px' }}
                                src={peopleDTO?.avatar} // Assuming `peopleDTO.avatar` contains the image data
                                onClick={handleAvatarEditClick}
                                sx={{ cursor: 'pointer', border: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={9} lg={10}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <TextFieldInput
                                        id='name'
                                        label={t('people.maintenance.name')}
                                        value={peopleDTO?.name}
                                        setValue={setPeopleDTO}
                                        obj={peopleDTO}
                                        required={true}
                                        maxLength={200}
                                        autoFocus={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <TextFieldInput
                                        id='fullName'
                                        label={t('people.maintenance.fullName')}
                                        value={peopleDTO?.fullName}
                                        setValue={setPeopleDTO}
                                        obj={peopleDTO}
                                        required={true}
                                        maxLength={200}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <SelectField
                                        id='companyId'
                                        label={t('people.maintenance.company')}
                                        placeholder={t('people.maintenance.company')}
                                        value={peopleDTO?.companyId}
                                        setValue={setPeopleDTO}
                                        obj={peopleDTO}
                                        valueList={companyList}
                                        required={false}
                                        hasNoneSelect={false}
                                        readOnly={parentId ? true : false}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <SelectField
                                        id='typeId'
                                        label={t('people.maintenance.type')}
                                        placeholder={t('people.maintenance.type')}
                                        value={peopleDTO?.typeId}
                                        setValue={setPeopleDTO}
                                        obj={peopleDTO}
                                        valueList={typeList}
                                        required={true}
                                        hasNoneSelect={false}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <SelectField
                                        id='statusId'
                                        label={t('people.maintenance.status')}
                                        placeholder={t('people.maintenance.status')}
                                        value={peopleDTO?.statusId}
                                        setValue={setPeopleDTO}
                                        obj={peopleDTO}
                                        valueList={statusList}
                                        required={true}
                                        hasNoneSelect={false}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <SelectField
                                        id='departmentId'
                                        label={t('people.maintenance.department')}
                                        placeholder={t('people.maintenance.department')}
                                        value={peopleDTO?.departmentId}
                                        setValue={setPeopleDTO}
                                        obj={peopleDTO}
                                        valueList={departmentList}
                                        required={true}
                                        hasNoneSelect={false}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <SelectField
                                        id='jobTitleId'
                                        label={t('people.maintenance.jobTitle')}
                                        placeholder={t('people.maintenance.jobTitle')}
                                        value={peopleDTO?.jobTitleId}
                                        setValue={setPeopleDTO}
                                        obj={peopleDTO}
                                        valueList={jobTitleList}
                                        required={true}
                                        hasNoneSelect={false}
                                        searchable={true}
                                        labelKey='value'
                                        valueKey='id'
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextFieldInput
                                        id='phone'
                                        label={t('people.maintenance.phone')}
                                        value={peopleDTO?.phone}
                                        setValue={setPeopleDTO}
                                        obj={peopleDTO}
                                        maxLength={20}
                                        numericOnly={true}
                                        mask="+00 (00) 00000-0000"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextFieldInput
                                        id='mobile'
                                        label={t('people.maintenance.mobile')}
                                        value={peopleDTO?.mobile}
                                        setValue={setPeopleDTO}
                                        obj={peopleDTO}
                                        maxLength={20}
                                        numericOnly={true}
                                        mask="+00 (00) 00000-0000"
                                    />
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <TextFieldInput
                                        id='email'
                                        label={t('people.maintenance.email')}
                                        value={peopleDTO?.email}
                                        setValue={setPeopleDTO}
                                        obj={peopleDTO}
                                        maxLength={200}
                                        type='email'
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <TextFieldInput
                                id='description'
                                label={t('people.maintenance.description')}
                                value={peopleDTO?.description}
                                setValue={setPeopleDTO}
                                obj={peopleDTO}
                                multiline={true}
                                rows={2}
                            />
                        </Grid>
                        {peopleDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label={t('people.maintenance.id')}
                                        value={peopleDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label={t('people.maintenance.createdBy')}
                                        value={userList.find(x => x.id === peopleDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label={t('people.maintenance.creationDateTime')}
                                        value={formatDateTime(String(peopleDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label={t('people.maintenance.updatedBy')}
                                        value={userList.find(x => x.id === peopleDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label={t('people.maintenance.updateDateTime')}
                                        value={formatDateTime(String(peopleDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                        {/* ... restante do JSX ... */}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};
